/* eslint-disable react-hooks/exhaustive-deps */
import { gql, useLazyQuery } from '@apollo/client';
import { Flex, SimpleGrid, Text, Tooltip, useToast } from '@chakra-ui/react';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { formatCurrency, formatNumber } from '../../../../utils/format';
import DataLoading from '../../../DataLoading';

const Q_DASHBOARD = gql`
	query get($companyType: InsuranceInstitutionsType, $companyId: String, $range: MDDataDateRangeInput) {
		getSaleDashboard(companyType: $companyType, companyId: $companyId, range: $range)
	}
`;

function Sidebar({ companyType = null, companyId = null, range = null }) {
	const toast = useToast();
	const { t } = useTranslation();
	const [getDashboard, { loading, data, error }] = useLazyQuery(Q_DASHBOARD, { fetchPolicy: 'no-cache' });

	useEffect(load, [companyType, companyId, range]);

	useEffect(() => {
		if (error) {
			if (error?.graphQLErrors?.length > 0) {
				error?.graphQLErrors.map(({ message }, i) =>
					toast({
						title: t(message),
						status: 'error',
						isClosable: true,
						position: 'bottom-right',
					}),
				);
			} else {
				toast({
					title: t(error?.message ? error?.message : 'something went wrong'),
					status: 'error',
					isClosable: true,
					position: 'bottom-right',
				});
			}
		}
	}, [error]);

	function load() {
		const variables = { companyType, companyId, range: range?.from && range?.to ? range : null };
		getDashboard({ variables });
	}

	return (
		<>
			<DataLoading loading={loading} loadedData={data?.getSaleDashboard}>
				{({ loadedData }) => (
					<SimpleGrid w="100%" columns={[1, 1, 3, 5]} spacing={4} border="1px dotted #bdc3c7" pl={4} pr={4} pt={4} pb={4}>
						<Flex width="100%" direction="column">
							<Tooltip label={<>{t('Gross Premium Amount (New)')}</>}>
								<Text fontSize={['md', 'sm', 'sm']} fontWeight="semibold" textAlign={['center', 'center', 'left']} textColor="gray.500">
									{t('Gross Premium Amount (New)')}
								</Text>
							</Tooltip>
							<Text
								fontSize={['4xl', '4xl', '3xl']}
								fontWeight="bold"
								textAlign={['center', 'center', 'left']}
								color={loadedData?.newGrossPremiumAmount?.color}
							>
								{formatCurrency(loadedData?.newGrossPremiumAmount?.value)}
							</Text>
						</Flex>
						<Flex width="100%" direction="column">
							<Tooltip label={<>{t('Gross Premium Amount (Renewal)')}</>}>
								<Text fontSize={['md', 'sm', 'sm']} fontWeight="semibold" textAlign={['center', 'center', 'left']} textColor="gray.500">
									{t('Gross Premium Amount (Renewal)')}
								</Text>
							</Tooltip>
							<Text
								fontSize={['4xl', '4xl', '3xl']}
								fontWeight="bold"
								textAlign={['center', 'center', 'left']}
								color={loadedData?.renewalGrossPremiumAmount?.color}
							>
								{formatCurrency(loadedData?.renewalGrossPremiumAmount?.value)}
							</Text>
						</Flex>
						<Flex width="100%" direction="column">
							<Tooltip label={<>{t('Sum Insured Amount (New)')}</>}>
								<Text fontSize={['md', 'sm', 'sm']} fontWeight="semibold" textAlign={['center', 'center', 'left']} textColor="gray.500">
									{t('Sum Insured Amount (New)')}
								</Text>
							</Tooltip>
							<Text
								fontSize={['4xl', '4xl', '3xl']}
								fontWeight="bold"
								textAlign={['center', 'center', 'left']}
								color={loadedData?.newSumInsuredAmount?.color}
							>
								{formatCurrency(loadedData?.newSumInsuredAmount?.value)}
							</Text>
						</Flex>
						<Flex width="100%" direction="column">
							<Tooltip label={<>{t('Sum Insured Amount (Renewal)')}</>}>
								<Text fontSize={['md', 'sm', 'sm']} fontWeight="semibold" textAlign={['center', 'center', 'left']} textColor="gray.500">
									{t('Sum Insured Amount (Renewal)')}
								</Text>
							</Tooltip>
							<Text
								fontSize={['4xl', '4xl', '3xl']}
								fontWeight="bold"
								textAlign={['center', 'center', 'left']}
								color={loadedData?.renewalSumInsuredAmount?.color}
							>
								{formatCurrency(loadedData?.renewalSumInsuredAmount?.value)}
							</Text>
						</Flex>
						<Flex width="100%" direction="column">
							<Tooltip label={<>{t('Number of Policies (New)')}</>}>
								<Text fontSize={['md', 'sm', 'sm']} fontWeight="semibold" textAlign={['center', 'center', 'left']} textColor="gray.500">
									{t('Number of Policies (New)')}
								</Text>
							</Tooltip>
							<Text
								fontSize={['4xl', '4xl', '3xl']}
								fontWeight="bold"
								textAlign={['center', 'center', 'left']}
								color={loadedData?.newNumberOfPolicies?.color}
							>
								{formatNumber(loadedData?.newNumberOfPolicies?.value)}
							</Text>
						</Flex>
						<Flex width="100%" direction="column">
							<Tooltip label={<>{t('Number of Policies (Renewal)')}</>}>
								<Text fontSize={['md', 'sm', 'sm']} fontWeight="semibold" textAlign={['center', 'center', 'left']} textColor="gray.500">
									{t('Number of Policies (Renewal)')}
								</Text>
							</Tooltip>
							<Text
								fontSize={['4xl', '4xl', '3xl']}
								fontWeight="bold"
								textAlign={['center', 'center', 'left']}
								color={loadedData?.renewalNumberOfPolicies?.color}
							>
								{formatNumber(loadedData?.renewalNumberOfPolicies?.value)}
							</Text>
						</Flex>
						<Flex width="100%" direction="column">
							<Tooltip label={<>{t('Number of Insured Person Person (Female)')}</>}>
								<Text fontSize={['md', 'sm', 'sm']} fontWeight="semibold" textAlign={['center', 'center', 'left']} textColor="gray.500">
									{t('Number of Insured Person Person (Female)')}
								</Text>
							</Tooltip>
							<Text
								fontSize={['4xl', '4xl', '3xl']}
								fontWeight="bold"
								textAlign={['center', 'center', 'left']}
								color={loadedData?.numberOfInsuredPersonFemale?.color}
							>
								{formatNumber(loadedData?.numberOfInsuredPersonFemale?.value)}
							</Text>
						</Flex>
						<Flex width="100%" direction="column">
							<Tooltip label={<>{t('Number of Insured Person Person (Male)')}</>}>
								<Text fontSize={['md', 'sm', 'sm']} fontWeight="semibold" textAlign={['center', 'center', 'left']} textColor="gray.500">
									{t('Number of Insured Person Person (Male)')}
								</Text>
							</Tooltip>
							<Text
								fontSize={['4xl', '4xl', '3xl']}
								fontWeight="bold"
								textAlign={['center', 'center', 'left']}
								color={loadedData?.numberOfInsuredPersonMale?.color}
							>
								{formatNumber(loadedData?.numberOfInsuredPersonMale?.value)}
							</Text>
						</Flex>
						<Flex width="100%" direction="column">
							<Tooltip label={<>{t('Number of Cancellation (Life)')}</>}>
								<Text
									fontSize={['md', 'sm', 'sm']}
									noOfLines={2}
									fontWeight="semibold"
									textAlign={['center', 'center', 'left']}
									textColor="gray.500"
								>
									{t('Number of Cancellation (Life)')}
								</Text>
							</Tooltip>
							<Text
								fontSize={['4xl', '4xl', '3xl']}
								fontWeight="bold"
								textAlign={['center', 'center', 'left']}
								color={loadedData?.numberOfCancellationFlat?.color}
							>
								{formatNumber(loadedData?.numberOfCancellationFlat?.value)}
							</Text>
						</Flex>
						<Flex width="100%" direction="column">
							<Tooltip label={<>{t('Surrender Policies')}</>}>
								<Text fontSize={['md', 'sm', 'sm']} fontWeight="semibold" textAlign={['center', 'center', 'left']} textColor="gray.500">
									{t('Surrender Policies')}
								</Text>
							</Tooltip>
							<Text
								fontSize={['4xl', '4xl', '3xl']}
								fontWeight="bold"
								textAlign={['center', 'center', 'left']}
								color={loadedData?.numberOfCancellationSurrender?.color}
							>
								{formatNumber(loadedData?.numberOfCancellationSurrender?.value)}
							</Text>
						</Flex>
					</SimpleGrid>
				)}
			</DataLoading>
		</>
	);
}

export default Sidebar;
